(() => {
  "use strict";

  const SELECTOR = "[data-sf-timer-popup]";

  const SEGMENTS_BY_NUMBER = {
    0: ["a", "b", "c", "d", "e", "f"],
    1: ["b", "c"],
    2: ["a", "b", "d", "e", "g"],
    3: ["a", "b", "c", "d", "g"],
    4: ["b", "c", "f", "g"],
    5: ["a", "c", "d", "f", "g"],
    6: ["a", "c", "d", "e", "f", "g"],
    7: ["a", "b", "c"],
    8: ["a", "b", "c", "d", "e", "f", "g"],
    9: ["a", "b", "c", "d", "f", "g"]
  };

  function readNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  class SplitflaskTimerPopup {
    constructor(root) {
      if (!root || root.dataset.sfInitialized === "true") {
        return;
      }

      root.dataset.sfInitialized = "true";

      this.root = root;
      this.dialog = root.querySelector(".sf-timer-popup__artboard");
      this.clock = root.querySelector("[data-sf-clock]");
      this.digits = Array.from(root.querySelectorAll(".sf-digit"));
      this.playButton = root.querySelector("[data-sf-play]");
      this.buttonLabel = root.querySelector("[data-sf-button-label]");
      this.initialState = root.querySelector("[data-sf-initial-state]");
      this.resultState = root.querySelector("[data-sf-result-state]");
      this.resultHeading = root.querySelector("[data-sf-result-heading]");
      this.resultMessage = root.querySelector("[data-sf-result-message]");
      this.dismissButtons = Array.from(root.querySelectorAll("[data-sf-dismiss]"));

      this.config = {
        enabled: root.dataset.enabled === "true",
        previewEnabled: root.dataset.previewEnabled === "true",
        triggerMode: root.dataset.triggerMode?.trim() || "either",
        initialDelay: readNumber(root.dataset.initialDelay, 10000),
        scrollThreshold: readNumber(root.dataset.scrollThreshold, 25),
        scrollIdleDelay: readNumber(root.dataset.scrollIdleDelay, 900),
        millisecondsPerDisplayedSecond: readNumber(root.dataset.countdownSpeed, 650),
        resultDelay: readNumber(root.dataset.resultDelay, 2000),
        stopLabel: root.dataset.stopLabel?.trim() || "STOP THE CLOCK",
        resultMessage: root.dataset.resultMessage?.trim() || "But we've got something for you.",
        winnerFormId: root.dataset.winnerFormId?.trim() || "",
        standardFormId: root.dataset.standardFormId?.trim() || "",
        blockedPopupSelectors: root.dataset.blockedPopupSelectors?.trim() || "[aria-modal='true'], .klaviyo-form, .modal, .popup, .drawer[open]",
        dismissDays: readNumber(root.dataset.dismissDays, 30),
        playedDays: readNumber(root.dataset.playedDays, 90),
        isSubscriber: root.dataset.isSubscriber === "true"
      };

      this.storageKeys = {
        shown: "splitflask_timer_shown_session",
        dismissed: "splitflask_timer_dismissed_until",
        played: "splitflask_timer_played_until",
        subscriber: "splitflask_marketing_subscriber"
      };

      this.startingHundredths = 1000;
      this.winningHundredths = 200;
      this.remainingHundredths = this.startingHundredths;

      this.running = false;
      this.completed = false;
      this.opened = false;
      this.klaviyoRequested = false;
      this.animationFrame = null;
      this.resultTimeout = null;
      this.klaviyoObserver = null;
      this.klaviyoFallbackTimeout = null;
      this.previousFocus = null;
      this.triggerStartTimeout = null;
      this.blockedRetryTimeout = null;
      this.scrollIdleTimeout = null;
      this.minimumTimeReached = false;
      this.scrollThresholdReached = false;
      this.scrollIdle = true;
      this.pageStartTime = performance.now();

      this.handlePlayClick = this.handlePlayClick.bind(this);
      this.handlePlayPointerDown = this.handlePlayPointerDown.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.updateTimer = this.updateTimer.bind(this);
      this.handleTriggerActivity = this.handleTriggerActivity.bind(this);
      this.handleTriggerScroll = this.handleTriggerScroll.bind(this);
      this.handleTriggerResize = this.handleTriggerResize.bind(this);
      this.evaluateTriggerConditions = this.evaluateTriggerConditions.bind(this);
      this.handleKlaviyoFormActivity = this.handleKlaviyoFormActivity.bind(this);

      this.createDigitSegments();
      this.renderTime(this.startingHundredths);
      this.bindEvents();
      this.scheduleOpen();
    }

    isPaidTraffic() {
      /*
       * Skip the popup for visitors who arrived from a paid ad
       * (Google, Meta, TikTok click IDs or a paid utm_medium).
       * Remembered for the rest of the browser session.
       */
      const key = "splitflask_timer_paid_session";

      if (this.readSession(key) === "true") return true;

      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return false;
      }

      const clickIds = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid"];
      const medium = (params.get("utm_medium") || "").toLowerCase();
      const isPaid =
        clickIds.some((id) => params.has(id)) ||
        /^(cpc|ppc|paid)/.test(medium);

      if (isPaid) {
        this.writeSession(key, "true");
      }

      return isPaid;
    }

    isDesignMode() {
      return Boolean(window.Shopify?.designMode);
    }

    createDigitSegments() {
      this.digits.forEach((digit) => {
        if (digit.children.length > 0) return;

        ["a", "b", "c", "d", "e", "f", "g"].forEach((segmentName) => {
          const segment = document.createElement("span");
          segment.className = `sf-digit-segment sf-digit-segment--${segmentName}`;
          segment.dataset.segment = segmentName;
          segment.setAttribute("aria-hidden", "true");
          digit.appendChild(segment);
        });
      });
    }

    bindEvents() {
      this.playButton?.addEventListener("click", this.handlePlayClick);
      this.playButton?.addEventListener("pointerdown", this.handlePlayPointerDown, {
        passive: false
      });

      this.dismissButtons.forEach((button) => {
        button.addEventListener("click", () => {
          if (!this.completed) {
            this.dismiss();
          }
        });
      });

      document.addEventListener("keydown", this.handleKeydown);
      window.addEventListener("klaviyoForms", this.handleKlaviyoFormActivity);
    }

    scheduleOpen() {
      const canPreview = this.isDesignMode() && this.config.previewEnabled;

      if (!this.config.enabled && !canPreview) return;
      if (!this.isEligible()) return;

      this.scrollThresholdReached = this.getScrollProgress() >= this.config.scrollThreshold;
      this.startTriggerMonitoring();
      this.evaluateTriggerConditions();
    }

    isEligible() {
      const path = window.location.pathname.toLowerCase();
      const excludedPaths = ["/cart", "/checkout", "/checkouts", "/account", "/orders", "/thank_you"];

      if (excludedPaths.some((item) => path.includes(item))) {
        return false;
      }

      if (this.isPaidTraffic()) {
        return false;
      }

      if (this.isSubscriber()) {
        return false;
      }

      if (window.__splitflaskTimerPopupClaimed === true) {
        return false;
      }

      if (this.readSession(this.storageKeys.shown) === "true") {
        return false;
      }

      const now = Date.now();

      if (this.readLocalNumber(this.storageKeys.dismissed) > now) {
        return false;
      }

      if (this.readLocalNumber(this.storageKeys.played) > now) {
        return false;
      }

      return true;
    }

    open({ force = false } = {}) {
      if (this.opened) return false;

      if (!force && !this.isEligible()) {
        this.stopTriggerMonitoring();
        return false;
      }

      this.opened = true;
      this.stopTriggerMonitoring();
      this.previousFocus = document.activeElement;
      window.__splitflaskTimerPopupClaimed = true;

      this.root.classList.add("is-open");
      this.root.setAttribute("aria-hidden", "false");
      document.body.classList.add("sf-timer-popup-open");

      this.writeSession(this.storageKeys.shown, "true");

      window.setTimeout(() => {
        this.playButton?.focus();
      }, 50);

      this.dispatch("splitflask:timer_open");
      return true;
    }

    close() {
      this.running = false;

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      this.root.classList.remove("is-open");
      this.root.setAttribute("aria-hidden", "true");
      document.body.classList.remove("sf-timer-popup-open");

      this.opened = false;

      if (this.previousFocus && typeof this.previousFocus.focus === "function") {
        this.previousFocus.focus();
      }
    }

    dismiss() {
      if (this.completed) return;

      this.writeSuppression(this.storageKeys.dismissed, this.config.dismissDays);

      this.dispatch("splitflask:timer_dismiss");
      this.close();
    }

    handlePlayClick() {
      if (this.completed) return;

      if (!this.running) {
        this.start();
      } else {
        this.stop();
      }
    }

    handlePlayPointerDown(event) {
      if (!this.running || this.completed) return;

      event.preventDefault();
      this.stop();
    }

    startTriggerMonitoring() {
      window.addEventListener("scroll", this.handleTriggerScroll, { passive: true });
      window.addEventListener("wheel", this.handleTriggerActivity, { passive: true });
      window.addEventListener("touchmove", this.handleTriggerActivity, { passive: true });
      window.addEventListener("resize", this.handleTriggerResize, { passive: true });
      window.addEventListener("orientationchange", this.handleTriggerResize, { passive: true });

      if (this.triggerStartTimeout) {
        clearTimeout(this.triggerStartTimeout);
        this.triggerStartTimeout = null;
      }

      if (this.blockedRetryTimeout) {
        clearTimeout(this.blockedRetryTimeout);
        this.blockedRetryTimeout = null;
      }

      const delay = Math.max(0, Number(this.config.initialDelay) || 0);

      this.triggerStartTimeout = window.setTimeout(() => {
        this.minimumTimeReached = true;
        this.evaluateTriggerConditions();
      }, delay);

    }

    stopTriggerMonitoring() {
      if (this.triggerStartTimeout) {
        clearTimeout(this.triggerStartTimeout);
        this.triggerStartTimeout = null;
      }

      if (this.blockedRetryTimeout) {
        clearTimeout(this.blockedRetryTimeout);
        this.blockedRetryTimeout = null;
      }

      if (this.scrollIdleTimeout) {
        clearTimeout(this.scrollIdleTimeout);
        this.scrollIdleTimeout = null;
      }

      window.removeEventListener("scroll", this.handleTriggerScroll);
      window.removeEventListener("wheel", this.handleTriggerActivity);
      window.removeEventListener("touchmove", this.handleTriggerActivity);
      window.removeEventListener("resize", this.handleTriggerResize);
      window.removeEventListener("orientationchange", this.handleTriggerResize);
    }

    handleTriggerScroll() {
      this.scrollThresholdReached = this.getScrollProgress() >= this.config.scrollThreshold;
      this.scrollIdle = false;

      if (this.scrollIdleTimeout) {
        clearTimeout(this.scrollIdleTimeout);
      }

      this.scrollIdleTimeout = window.setTimeout(() => {
        this.scrollIdle = true;
        this.evaluateTriggerConditions();
      }, Math.max(0, Number(this.config.scrollIdleDelay) || 0));

      this.evaluateTriggerConditions();
    }

    handleTriggerActivity() {
      this.scrollIdle = false;

      if (this.scrollIdleTimeout) {
        clearTimeout(this.scrollIdleTimeout);
      }

      this.scrollIdleTimeout = window.setTimeout(() => {
        this.scrollIdle = true;
        this.evaluateTriggerConditions();
      }, Math.max(0, Number(this.config.scrollIdleDelay) || 0));
    }

    handleTriggerResize() {
      this.scrollThresholdReached = this.getScrollProgress() >= this.config.scrollThreshold;
      this.evaluateTriggerConditions();
    }

    getScrollProgress() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      const doc = document.documentElement;
      const scrollableHeight = Math.max(0, (doc.scrollHeight || 0) - window.innerHeight);

      if (scrollableHeight <= 0) {
        return 0;
      }

      return Math.min(100, Math.max(0, (scrollTop / scrollableHeight) * 100));
    }

    isOtherPopupVisible() {
      const selectors = this.config.blockedPopupSelectors
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);

      const fallbackSelectors = ["[aria-modal='true']", ".klaviyo-form", ".modal", ".popup", ".drawer[open]"];
      const combined = [...new Set([...fallbackSelectors, ...selectors])];

      return combined.some((selector) => {
        try {
          return Array.from(document.querySelectorAll(selector)).some((element) => {
            if (!(element instanceof Element)) return false;
            if (element.closest(SELECTOR)) return false;

            const styles = window.getComputedStyle(element);
            const rect = element.getBoundingClientRect();

            return (
              styles.display !== "none" &&
              styles.visibility !== "hidden" &&
              Number(styles.opacity) !== 0 &&
              rect.width > 0 &&
              rect.height > 0
            );
          });
        } catch (error) {
          return false;
        }
      });
    }

    evaluateTriggerConditions() {
      if (this.opened || this.completed) return;

      if (!this.isEligible()) {
        this.stopTriggerMonitoring();
        return;
      }

      const minimumTimeMet =
        this.minimumTimeReached ||
        (performance.now() - this.pageStartTime) >= Math.max(0, Number(this.config.initialDelay) || 0);
      const scrollThresholdMet =
        this.scrollThresholdReached ||
        this.getScrollProgress() >= this.config.scrollThreshold;
      const scrollIdleMet =
        this.scrollIdle || (Math.max(0, Number(this.config.scrollIdleDelay) || 0) === 0);
      const triggerMode = this.config.triggerMode;
      const triggerMet =
        triggerMode === "time"
          ? minimumTimeMet
          : triggerMode === "scroll"
            ? scrollThresholdMet
            : (minimumTimeMet || scrollThresholdMet);

      if (!triggerMet || !scrollIdleMet) return;

      if (!this.isOtherPopupVisible()) {
        this.open();
        return;
      }

      if (!this.blockedRetryTimeout) {
        this.blockedRetryTimeout = window.setTimeout(() => {
          this.blockedRetryTimeout = null;
          this.evaluateTriggerConditions();
        }, 2000);
      }
    }

    start() {
      if (this.running || this.completed) return;

      this.writeSuppression(this.storageKeys.played, this.config.playedDays);
      this.running = true;
      this.startedAt = performance.now();
      this.remainingHundredths = this.startingHundredths;

      if (this.buttonLabel) {
        this.buttonLabel.textContent = this.config.stopLabel || "STOP THE CLOCK";
      }

      this.dispatch("splitflask:timer_start");
      this.animationFrame = requestAnimationFrame(this.updateTimer);
    }

    calculateRemainingHundredths(now) {
      const elapsedMilliseconds = now - this.startedAt;
      const millisecondsPerHundredth = this.config.millisecondsPerDisplayedSecond / 100;
      const elapsedHundredths = Math.floor(elapsedMilliseconds / millisecondsPerHundredth);

      return Math.max(0, this.startingHundredths - elapsedHundredths);
    }

    updateTimer(now) {
      if (!this.running) return;

      this.remainingHundredths = this.calculateRemainingHundredths(now);

      this.renderTime(this.remainingHundredths);

      if (this.remainingHundredths <= 0) {
        this.stop();
        return;
      }

      this.animationFrame = requestAnimationFrame(this.updateTimer);
    }

    stop() {
      if (!this.running || this.completed) return;

      /*
       * Recalculate from the actual click timestamp.
       * Do not trust the previous animation frame.
       */
      const clickTimestamp = performance.now();

      this.remainingHundredths = this.calculateRemainingHundredths(clickTimestamp);

      this.running = false;
      this.completed = true;

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      this.renderTime(this.remainingHundredths);

      const won = this.remainingHundredths === this.winningHundredths;

      this.showResult(won);

      this.writeSuppression(this.storageKeys.played, this.config.playedDays);

      const detail = {
        stoppedAt: this.formatTime(this.remainingHundredths),
        stoppedAtHundredths: this.remainingHundredths,
        outcome: won ? "winner" : "standard"
      };

      this.dispatch("splitflask:timer_stop", detail);
      this.dispatch("splitflask:timer_result", detail);

      const formId = won ? this.config.winnerFormId : this.config.standardFormId;

      this.openKlaviyoForm(formId, detail);

      this.resultTimeout = window.setTimeout(() => {
        this.close();
      }, this.config.resultDelay);
    }

    showResult(won) {
      this.initialState.hidden = true;
      this.resultState.hidden = false;

      if (won) {
        this.resultHeading.textContent = "YOU DID IT!";
        this.resultMessage.textContent =
          "You stopped the clock at exactly 02:00. You won a free Splitflask.";
      } else {
        this.resultHeading.textContent = "SO CLOSE!";
        this.resultMessage.textContent = this.config.resultMessage;
      }
    }

    renderTime(totalHundredths) {
      const formatted = this.formatTime(totalHundredths);
      const characters = formatted.replace(":", "").split("");

      characters.forEach((character, index) => {
        this.renderDigit(this.digits[index], Number(character));
      });

      this.clock.setAttribute(
        "aria-label",
        `${formatted.slice(0, 2)} seconds and ${formatted.slice(3)} hundredths`
      );
    }

    renderDigit(element, number) {
      if (!element) return;

      const activeSegments = SEGMENTS_BY_NUMBER[number] || [];

      element.querySelectorAll("[data-segment]").forEach((segment) => {
        segment.classList.toggle("is-active", activeSegments.includes(segment.dataset.segment));
      });
    }

    formatTime(value) {
      const safeValue = Math.max(0, Math.floor(value));
      const seconds = Math.floor(safeValue / 100);
      const hundredths = safeValue % 100;

      return `${String(seconds).padStart(2, "0")}:${String(hundredths).padStart(2, "0")}`;
    }

    openKlaviyoForm(formId, detail) {
      if (this.klaviyoRequested) return;
      this.klaviyoRequested = true;

      if (!formId) {
        console.info("Splitflask timer: no Klaviyo form ID configured.", detail);
        return;
      }

      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", formId]);

      this.dispatch("splitflask:klaviyo_form_requested", {
        ...detail,
        formId
      });
    }

    handleKlaviyoFormActivity(event) {
      const detail = event?.detail;
      if (!detail || typeof detail !== "object") return;

      const type = detail.type;
      const metadata = detail.metaData && typeof detail.metaData === "object"
        ? detail.metaData
        : {};
      const submittedEmail = metadata.$email || metadata.email;
      const subscribed = type === "submit" || (type === "stepSubmit" && submittedEmail);

      if (!subscribed) return;

      this.writeLocal(this.storageKeys.subscriber, "true");
      this.stopTriggerMonitoring();

      if (this.opened) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (!this.opened) return;

      if (event.key === "Escape" && !this.completed) {
        event.preventDefault();
        this.dismiss();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        return !element.hidden && element.offsetParent !== null;
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    writeSuppression(key, days) {
      const expiration = Date.now() + days * 24 * 60 * 60 * 1000;

      this.writeLocal(key, String(expiration));
    }

    writeLocal(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.info("Splitflask timer: localStorage unavailable.");
      }
    }

    readLocal(key) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    readLocalNumber(key) {
      return Number(this.readLocal(key) || 0);
    }

    isSubscriber() {
      if (this.config.isSubscriber) return true;
      if (this.readLocal(this.storageKeys.subscriber) === "true") return true;

      const newsletterCookie = (document.cookie || "")
        .split(";")
        .some((cookie) => cookie.trim() === "newsletter-subscribed=true");

      if (newsletterCookie) {
        this.writeLocal(this.storageKeys.subscriber, "true");
        return true;
      }

      const successfulNewsletterForm = document.querySelector?.(
        '[data-sf-newsletter-subscribed="true"]'
      );

      if (!successfulNewsletterForm) return false;

      this.writeLocal(this.storageKeys.subscriber, "true");
      return true;
    }

    writeSession(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        console.info("Splitflask timer: sessionStorage unavailable.");
      }
    }

    readSession(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    dispatch(name, detail = {}) {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }

    destroy() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      if (this.resultTimeout) {
        clearTimeout(this.resultTimeout);
      }

      this.stopTriggerMonitoring();

      if (this.klaviyoObserver) {
        this.klaviyoObserver.disconnect();
        this.klaviyoObserver = null;
      }

      if (this.klaviyoFallbackTimeout) {
        clearTimeout(this.klaviyoFallbackTimeout);
        this.klaviyoFallbackTimeout = null;
      }

      this.playButton?.removeEventListener("click", this.handlePlayClick);
      this.playButton?.removeEventListener("pointerdown", this.handlePlayPointerDown);

      document.removeEventListener("keydown", this.handleKeydown);
      window.removeEventListener("klaviyoForms", this.handleKlaviyoFormActivity);

      this.close();
      this.root.dataset.sfInitialized = "false";
    }
  }

  function initialize(root = document) {
    root.querySelectorAll(SELECTOR).forEach((popup) => {
      popup.sfTimerInstance = new SplitflaskTimerPopup(popup);
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    initialize();
  });

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((popup) => {
      popup.sfTimerInstance?.destroy();
    });
  });

  window.SplitflaskTimerDebug = {
    open() {
      const popup = document.querySelector(SELECTOR);
      popup?.sfTimerInstance?.open({ force: true });
    },

    clearStorage() {
      [
        "splitflask_timer_shown_session",
        "splitflask_timer_dismissed_until",
        "splitflask_timer_played_until",
        "splitflask_marketing_subscriber",
        "splitflask_timer_paid_session"
      ].forEach((key) => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (error) {
          // Ignore unavailable storage.
        }
      });

      window.__splitflaskTimerPopupClaimed = false;

      console.info("Splitflask timer storage cleared.");
    }
  };
})();